import { useCallback, useEffect, useState } from 'react';

/**
 * L'evenement que Chrome/Edge emettent quand la page remplit les conditions
 * d'installation. Il n'est pas dans lib.dom.d.ts.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/*
 * SC-906 — l'evenement arrive AVANT que le carnet soit deverrouille : Chrome
 * l'emet des que la page est jugee installable, donc pendant le pave PIN, bien
 * avant que la liste des tables (et son bandeau) existe. On l'attrape au
 * chargement du module et on le garde ici ; les composants viennent le lire
 * quand ils se montent. Sans ce guichet, l'invitation ne s'afficherait qu'a la
 * loterie des reloads.
 */
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function announce(): void {
  listeners.forEach((notify) => notify());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sans preventDefault, Chrome peut afficher sa propre mini-barre : c'est
    // notre bandeau qui porte l'invitation, au moment choisi par le carnet.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    announce();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    announce();
  });
}

/** Le carnet tourne-t-il DEJA comme application posee sur l'ecran d'accueil ? */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const standaloneMedia = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // Le drapeau proprietaire d'iOS, seul indice disponible sur iPhone.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return standaloneMedia || iosStandalone;
}

/**
 * Ce navigateur connait-il l'installation automatique ?
 *
 * Test de capacite plutot que lecture du user-agent : `onbeforeinstallprompt`
 * n'existe que chez ceux qui savent ouvrir la boite native (Chrome, Edge,
 * Samsung Internet, Opera). C'est aussi ce qui evite de raconter la manoeuvre
 * manuelle a un Chrome dont l'evenement n'est pas ENCORE arrive — ou qui ne
 * l'emettra pas parce que le carnet est deja installe.
 */
export function supportsInstallPrompt(): boolean {
  return typeof window !== 'undefined' && 'onbeforeinstallprompt' in window;
}

/**
 * Safari (et tout navigateur sur iOS, qui utilise le meme moteur) n'offre
 * aucune API d'installation : la pose de l'icone passe obligatoirement par le
 * menu « Partager » de l'utilisateur. On ne peut donc que montrer le geste.
 */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ se presente comme un Macintosh ; le tactile le trahit.
  return /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
}

/**
 * Navigateur encapsule dans une autre application (WeChat, Messenger, Line,
 * Instagram, webview Android). Aucun d'eux ne pose d'icone : le lien du carnet
 * arrive souvent par messagerie, et le serveur reste coince dedans sans
 * comprendre pourquoi rien ne s'installe. La seule issue est d'ouvrir la page
 * dans un vrai navigateur, ce que la fiche explique.
 */
export function isInAppBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /micromessenger|weibo|qq\/|qqbrowser|line\/|instagram|fban|fbav|fb_iab|tiktok|snapchat/i.test(
    ua,
  );
}

function isHandheld(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) || isIosDevice();
}

/** Quelle manoeuvre manuelle montrer, faute de boite native. */
export type ManualStepsKind = 'ios' | 'browserMenu' | 'inAppBrowser';

export interface InstallAppState {
  /** Une boite d'installation native est disponible : un seul geste suffit. */
  canPrompt: boolean;
  /**
   * La manoeuvre a proposer SPONTANEMENT (la bande du haut), ou `null` quand
   * se taire vaut mieux qu'une consigne fausse.
   */
  manualSteps: ManualStepsKind | null;
  /**
   * La manoeuvre a montrer a qui vient la DEMANDER (la ligne permanente de la
   * feuille d'identite). Toujours renseignee : ce bouton est offert en
   * permanence, il doit donc repondre quelque chose a chaque appui.
   */
  stepsKind: ManualStepsKind;
  /** Deja installe (ou installe a l'instant) : plus rien a proposer. */
  isInstalled: boolean;
  /** Ouvre la boite native. Rend `false` s'il n'y en avait pas. */
  promptInstall: () => Promise<boolean>;
}

/**
 * Quelle fiche montrer quand aucune boite native ne s'ouvre — et il y en a
 * toujours une a montrer, y compris chez un Chrome qui sait pourtant l'ouvrir :
 * quand il n'emet pas l'evenement (carnet DEJA installe, ou criteres pas encore
 * remplis), son propre menu reste la seule issue, et c'est precisement le
 * moment ou le serveur vient chercher le bouton.
 */
export function resolveStepsKind(): ManualStepsKind {
  // Un navigateur encapsule ne pose aucune icone, meme sur iOS : il passe donc
  // AVANT le cas iPhone.
  if (isInAppBrowser()) return 'inAppBrowser';
  if (isIosDevice()) return 'ios';
  // Chrome reste muet, Firefox Android, Opera Mini, navigateurs constructeurs,
  // poste de bureau : le menu du navigateur sait le faire dans tous ces cas.
  return 'browserMenu';
}

function resolveManualSteps(): ManualStepsKind | null {
  // Un navigateur capable de la boite native n'a pas besoin qu'on l'aborde :
  // soit l'evenement est deja la (canPrompt), soit il arrive, soit le carnet
  // est deja installe. Dans les trois cas, l'invitation spontanee se tait ; la
  // ligne permanente, elle, repond toujours (`stepsKind`).
  if (supportsInstallPrompt()) return null;
  if (!isInAppBrowser() && !isIosDevice() && !isHandheld()) return null;
  return resolveStepsKind();
}

export function useInstallApp(): InstallAppState {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const notify = () => forceRender((n) => n + 1);
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const event = deferredPrompt;
    if (!event) return false;
    // Un evenement differe ne sert qu'une fois : le relacher tout de suite
    // evite un deuxieme appui sur un bouton devenu muet.
    deferredPrompt = null;
    announce();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted';
  }, []);

  const isInstalled = installed || isStandaloneDisplay();
  const canPrompt = !isInstalled && deferredPrompt !== null;

  return {
    canPrompt,
    manualSteps: isInstalled || canPrompt ? null : resolveManualSteps(),
    stepsKind: resolveStepsKind(),
    isInstalled,
    promptInstall,
  };
}
